import { type FacilityRole, type MeResponse, PARTICIPANT_ROLE, hasGmsAccess, isAdminRole, isFacilityRole } from "@seagames/contracts";
import { type LinkProps, redirect } from "@tanstack/react-router";
import { ApiError } from "../api/client";
import { hasStoredSession } from "./token-store";

/**
 * `beforeLoad` guard for admin-only pages (user admin, reference lists): a
 * session whose ACTIVE role is not SUPER_ADMIN / ADMIN goes to the dashboard.
 * One active role per session — the roles a user merely holds do not count.
 * The API answers 403 regardless; this only keeps people off a page of errors.
 */
export function requireAdminRole(session: MeResponse): void {
	if (!isAdminRole(session.active_role)) throw redirect({ to: "/" });
}

/**
 * Whether a failed `/auth/me` read in the shell's guard means "signed out":
 * only when the server ruled on the credential (`credentialVerdict` — the
 * client has already cleared the tokens) or no session is stored any more.
 * A 503, a 500, a network error keep the user where they are: the tokens
 * still stand, and the error boundary offers "try again".
 */
export function sessionLostOn(err: unknown): boolean {
	return (err instanceof ApiError && err.credentialVerdict) || !hasStoredSession();
}

/** `beforeLoad` guard for SUPER_ADMIN-only pages (the sign-in log): anyone else goes to the dashboard. */
export function requireSuperAdmin(session: MeResponse): void {
	if (session.active_role !== "SUPER_ADMIN") throw redirect({ to: "/" });
}

/**
 * Game Settings (every `/settings/…` page) is SUPER_ADMIN only (user,
 * 2026-10-30): anyone else, ADMIN included, goes to the dashboard. One
 * check in the shell's guard, so a page added under `/settings` is covered
 * without its own `beforeLoad`.
 */
export function gameSettingsRedirect(session: MeResponse, pathname: string): void {
	if ((pathname === "/settings" || pathname.startsWith("/settings/")) && session.active_role !== "SUPER_ADMIN") throw redirect({ to: "/" });
}

/**
 * A facility login (a transport company, a hotel — M12 adds dining)
 * works on ONE module: its home. It lands there, and any other page (the
 * dashboard, accreditation, settings, a typed URL) sends it back instead of
 * rendering an empty page — every card predicate is FALSE for it anyway.
 * Its own profile and password stay open. `Record<FacilityRole, …>`: a new
 * facility role fails typecheck until it is given a home.
 */
const FACILITY_HOME: Record<FacilityRole, NonNullable<LinkProps["to"]>> = {
	TRANSPORT_PROVIDER: "/logistics/transportation",
	// M11: a hotel's front desk — arriving, in house, departing; its Helpdesk tab sits under it.
	HOTEL: "/accommodation",
};
const ANY_LOGIN_PATHS = ["/profile", "/change-password", "/changelog"] as const;
/** Old addresses that now live under a facility home: resolved before the home redirect, so a bookmark still lands on its page. */
const LEGACY_ALIASES: Readonly<Record<string, NonNullable<LinkProps["to"]>>> = { "/incident-helpdesk": "/accommodation/helpdesk" };

export function facilityRoleRedirect(session: MeResponse, pathname: string): void {
	const role = session.active_role;
	// No access at all is the "no access" page's job, not a redirect (it would loop).
	if (!role || !isFacilityRole(role) || !hasGmsAccess(session)) return;
	const home = FACILITY_HOME[role];
	const under = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
	const alias = LEGACY_ALIASES[pathname.replace(/\/$/, "")];
	if (alias && (alias === home || String(alias).startsWith(`${home}/`))) throw redirect({ to: alias });
	if (!under(home) && !ANY_LOGIN_PATHS.some(under)) throw redirect({ to: home });
}

/** The participant's own pages (`/me…`). */
export const PARTICIPANT_HOME = "/me";
const underMe = (pathname: string) => pathname === PARTICIPANT_HOME || pathname.startsWith(`${PARTICIPANT_HOME}/`);

/**
 * A PARTICIPANT session has its own small shell (profile, events, medals):
 * any staff address sends it to its profile, and a staff session that types
 * a `/me` address goes to the staff app. The API answers 403 regardless
 * (`/me` is PARTICIPANT only, every staff route fences on GMS_STAFF_ROLES).
 */
export function participantRedirect(session: MeResponse, pathname: string): void {
	const participant = session.active_role === PARTICIPANT_ROLE;
	if (participant && !underMe(pathname)) throw redirect({ to: PARTICIPANT_HOME, replace: true });
	if (!participant && underMe(pathname)) throw redirect({ to: "/", replace: true });
}
